// Wallet error suppressor: prevent noisy MetaMask connection errors from surfacing as
// uncaught promise rejections in the console. Initialises on import.

/*
	Use tabs for indentation in this file to align with project preference.
*/

const SUPPRESSED_SUBSTRINGS = [
	"failed to connect to metamask",
	"metamask extension not found",
	// Cancelled-vault-creation rejections from the Vultisig SDK retry
	// loops surface as multiple "Aborted" / "AbortError" rejections
	// after we tear the WASM instances down. They are expected and
	// already handled by the create flow's catch().
	"vault creation aborted",
	"vault creation cancelled",
	// Native AbortController default text — the SDK's relay polling
	// loops surface this when we tear down the in-memory MPC relay
	// after the vault has already been created.
	"signal is aborted",
	"the operation was aborted",
	"the user aborted a request",
];

const SUPPRESSED_NAMES = [
	"aborterror",
];

const toLowerMessage = (reason) => {
	try {
		if (!reason) return "";
		if (typeof reason === "string") return reason.toLowerCase();
		if (reason instanceof Error) return (reason.message || String(reason)).toLowerCase();
		// Generic object with message/cause
		if (typeof reason === "object") {
			const msg = reason.message || reason.cause || reason.reason || "";
			return String(msg).toLowerCase();
		}
		return String(reason).toLowerCase();
	} catch (_e) {
		return "";
	}
};

const toLowerName = (reason) => {
	try {
		if (!reason) return "";
		if (reason instanceof Error) return String(reason.name || "").toLowerCase();
		if (typeof reason === "object") return String(reason.name || "").toLowerCase();
		return "";
	} catch (_e) {
		return "";
	}
};

export const shouldSuppressWalletError = (reason) => {
	const msg = toLowerMessage(reason);
	const name = toLowerName(reason);
	if (msg && SUPPRESSED_SUBSTRINGS.some((needle) => msg.includes(needle))) return true;
	if (name && SUPPRESSED_NAMES.includes(name)) return true;
	if (!msg) return false;
	// Bare "aborted" string from WASM tear-down — only suppress when
	// it isn't part of a longer descriptive error.
	if (msg.trim() === "aborted") return true;
	return false;
};

export const initWalletErrorSuppressor = () => {
	if (typeof window === "undefined") return;
	// Ensure we only attach once
	if (window.__WINBIT32_WALLET_ERROR_SUPPRESSOR__) return;
	Object.defineProperty(window, "__WINBIT32_WALLET_ERROR_SUPPRESSOR__", {
		value: true,
		writable: false,
		configurable: false,
		enumerable: false,
	});

	window.addEventListener(
		"unhandledrejection",
		(event) => {
			try {
				if (shouldSuppressWalletError(event?.reason)) {
					// Prevent the default red error in console while still allowing debugging when needed
					// eslint-disable-next-line no-console
					console.info("Suppressed expected wallet/vault rejection (no action needed).");
					event.preventDefault();
					// Stop the webpack-dev-server overlay's bubble-phase
					// listener from picking the rejection back up.
					event.stopImmediatePropagation?.();
				}
			} catch (_e) {
				// Never throw from the handler
			}
		},
		{ capture: true }
	);

	// Vultisig SDK / wasm tear-down on abort sometimes throws into the
	// uncaught-error channel rather than the rejection channel; the dev
	// overlay then renders it as "Uncaught runtime error: Aborted".
	window.addEventListener(
		"error",
		(event) => {
			try {
				if (shouldSuppressWalletError(event?.error || event?.message)) {
					// eslint-disable-next-line no-console
					console.info("Suppressed expected post-abort runtime error (no action needed).");
					event.preventDefault();
					event.stopImmediatePropagation?.();
				}
			} catch (_e) {
				// Never throw from the handler
			}
		},
		{ capture: true }
	);
};

// Auto-initialise on import
initWalletErrorSuppressor();

export default initWalletErrorSuppressor;


